import React, { useState, useEffect } from 'react';
import { carePlansAPI } from '../services/api';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import SignaturePad from './SignaturePad';
import { Plus, Trash, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const VisitCompletionDialog = ({ open, onOpenChange, visit, clientId, onSubmit, submitting }) => {
  const [tasks, setTasks] = useState([]);
  const [newTaskText, setNewTaskText] = useState('');
  const [caregiverSig, setCaregiverSig] = useState('');
  const [clientSig, setClientSig] = useState('');
  const [completionNotes, setCompletionNotes] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && clientId) {
      loadCarePlanTasks();
      setCaregiverSig('');
      setClientSig('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientId, visit?.scheduled_date]);

  const getVisitDayName = () => {
    const dateStr = visit?.scheduled_date;
    if (!dateStr) return null;
    // Parse YYYY-MM-DD as local date to avoid timezone drift
    const parts = String(dateStr).split('-');
    if (parts.length !== 3) return null;
    const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    return ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][d.getDay()];
  };

  const loadCarePlanTasks = async () => {
    setLoading(true);
    try {
      const res = await carePlansAPI.getAll(clientId);
      const visitDay = getVisitDayName();
      const collected = [];
      (res.data || []).filter(p => p.is_active !== false).forEach(plan => {
        // Prefer structured tasks; fall back to legacy activities (strings)
        const structured = Array.isArray(plan.tasks) && plan.tasks.length > 0;
        if (structured) {
          plan.tasks.forEach((t, i) => {
            const days = Array.isArray(t.days_of_week) ? t.days_of_week : [];
            if (days.length > 0 && visitDay && !days.includes(visitDay)) return;
            collected.push({
              id: `cp_${plan.id}_${i}`,
              name: t.name,
              completed: false,
              from_care_plan: true,
              required: !!t.required,
              notes: t.notes || '',
              plan_title: plan.title,
            });
          });
        } else {
          (plan.activities || []).forEach((act, i) => {
            collected.push({
              id: `cp_${plan.id}_${i}`,
              name: act,
              completed: false,
              from_care_plan: true,
              required: false,
              notes: '',
              plan_title: plan.title,
            });
          });
        }
      });
      setTasks(collected);
    } catch (e) {
      console.error(e);
      setTasks([]);
    } finally { setLoading(false); }
  };

  const toggleTask = (idx) => {
    setTasks(tasks.map((t, i) => i === idx ? { ...t, completed: !t.completed } : t));
  };

  const updateTaskNotes = (idx, notes) => {
    setTasks(tasks.map((t, i) => i === idx ? { ...t, notes } : t));
  };

  const addTask = () => {
    const text = newTaskText.trim();
    if (!text) return;
    setTasks([...tasks, { id: `custom_${Date.now()}`, name: text, completed: true, from_care_plan: false, required: false, notes: '' }]);
    setNewTaskText('');
  };

  const removeTask = (idx) => {
    setTasks(tasks.filter((_, i) => i !== idx));
  };

  const unmetRequired = tasks.filter(t => t.required && !t.completed);

  const handleSubmit = () => {
    if (tasks.length === 0) { toast.error('Add at least one task'); return; }
    if (unmetRequired.length > 0) {
      toast.error(`Please complete ${unmetRequired.length} required task${unmetRequired.length > 1 ? 's' : ''} before clocking out`);
      return;
    }
    if (!caregiverSig) { toast.error('Caregiver signature is required'); return; }
    if (!clientSig) { toast.error('Client signature is required'); return; }
    onSubmit({ tasks_completed: tasks, caregiver_signature: caregiverSig, client_signature: clientSig, completion_notes: completionNotes.trim() || null });
  };

  const completedCount = tasks.filter(t => t.completed).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Complete Visit & Clock Out</DialogTitle>
          <DialogDescription>
            Review tasks completed during this visit and collect signatures. All fields are required to clock out.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Tasks Checklist */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Tasks Completed <span className="text-muted-foreground font-normal">({completedCount}/{tasks.length})</span></Label>
              {unmetRequired.length > 0 && (
                <span className="text-[11px] text-red-600 font-medium" data-testid="unmet-required-warning">
                  {unmetRequired.length} required task{unmetRequired.length > 1 ? 's' : ''} remaining
                </span>
              )}
            </div>
            {loading ? (
              <div className="text-center py-4 text-sm text-muted-foreground">Loading care plan tasks...</div>
            ) : tasks.length === 0 ? (
              <div className="text-center py-4 text-sm text-muted-foreground rounded-xl bg-muted/30 border border-dashed border-border">
                No care plan tasks — add tasks below
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto rounded-xl border border-border p-3" data-testid="tasks-list">
                {tasks.map((task, idx) => (
                  <div key={task.id || idx} className={`p-2 rounded-lg transition-colors ${task.completed ? 'bg-green-50' : 'bg-muted/30'}`} data-testid={`task-row-${idx}`}>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={task.completed}
                        onCheckedChange={() => toggleTask(idx)}
                        className="mt-0.5"
                        data-testid={`task-checkbox-${idx}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${task.completed ? 'font-medium' : 'text-muted-foreground'}`}>
                          {task.name}
                          {task.from_care_plan && <span className="ml-2 text-[10px] uppercase tracking-wider text-primary">Care Plan</span>}
                          {task.required && <span className="ml-2 text-[10px] uppercase tracking-wider text-red-600 font-semibold">Required</span>}
                        </p>
                        <Input
                          value={task.notes}
                          onChange={(e) => updateTaskNotes(idx, e.target.value)}
                          placeholder="Notes (optional)..."
                          className="h-7 text-xs rounded-lg mt-1"
                          data-testid={`task-notes-${idx}`}
                        />
                      </div>
                      {!task.from_care_plan && (
                        <Button type="button" variant="ghost" size="sm" className="rounded-xl h-7 px-2 text-destructive hover:text-destructive" onClick={() => removeTask(idx)} data-testid={`remove-task-${idx}`}>
                          <Trash weight="bold" className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add custom task */}
            <div className="flex gap-2">
              <Input
                value={newTaskText}
                onChange={(e) => setNewTaskText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTask(); } }}
                placeholder="Add extra task (e.g. Grocery run)..."
                className="rounded-xl"
                data-testid="new-task-input"
              />
              <Button type="button" variant="outline" className="rounded-xl gap-1" onClick={addTask} data-testid="add-task-btn">
                <Plus weight="bold" className="w-3.5 h-3.5" /> Add
              </Button>
            </div>
          </div>

          {/* Visit comments */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Visit Comments (optional)</Label>
            <Textarea
              value={completionNotes}
              onChange={(e) => setCompletionNotes(e.target.value)}
              placeholder="How did the visit go? Anything the office or family should know (mood, appetite, concerns, follow-ups)…"
              rows={3}
              className="rounded-xl"
              data-testid="completion-notes-input"
            />
          </div>

          {/* Signatures */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SignaturePad label="Caregiver Signature *" onChange={setCaregiverSig} testId="caregiver-signature" />
            <SignaturePad label="Client Signature *" onChange={setClientSig} testId="client-signature" />
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="button"
              className="flex-1 rounded-xl gap-2"
              onClick={handleSubmit}
              disabled={submitting || !caregiverSig || !clientSig || tasks.length === 0 || unmetRequired.length > 0}
              data-testid="submit-completion-btn"
            >
              <CheckCircle weight="bold" className="w-4 h-4" />
              {submitting ? 'Clocking Out...' : 'Submit & Clock Out'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VisitCompletionDialog;
